import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { usePartnerAuth } from '../../context/PartnerAuthContext'

const MIN_PASSWORD_LENGTH = 6
const MAX_PASSWORD_LENGTH = 72

const PartnerLoginPage: React.FC = () => {
  const handleContactWhatsApp = () => {
    window.open('https://wa.me/message/LMQEI75YYCOBA1', '_blank')
  }
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshProfile } = useAuth()
  const { isPartner, loading: partnerLoading } = usePartnerAuth()

  // Si déjà connecté en tant que partenaire, aller au dashboard
  if (!partnerLoading && isPartner) {
    navigate('/partner/dashboard', { replace: true })
  }

  // Si on vient d'une autre page (ex: /partner/dashboard), on y retournera après login
  const from = (location.state as any)?.from?.pathname || '/partner/dashboard'

  // État du formulaire
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const isPasswordValid =
    password.length >= MIN_PASSWORD_LENGTH && password.length <= MAX_PASSWORD_LENGTH

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    try {
      // 1. Connexion Supabase
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (authError) {
        if (authError.message.toLowerCase().includes('email not confirmed')) {
          setError(
            "Votre adresse email n'est pas encore confirmée. Vérifie vos emails et cliquez sur le lien de confirmation."
          )
        } else {
          setError('Identifiants incorrects ou compte inexistant.')
        }
        setLoading(false)
        return
      }

      // 2. Recharger le profil pour obtenir les infos partenaire
      try {
        await refreshProfile()
      } catch (err) {
        console.error('Erreur refreshProfile:', err)
      }

      // 3. Attendre un peu pour que le contexte se mette à jour
      setTimeout(() => {
        setLoading(false)
        navigate(from, { replace: true })
      }, 300)
    } catch (err) {
      console.error('Erreur connexion:', err)
      setError('Une erreur est survenue lors de la connexion.')
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        maxWidth: '480px',
        margin: '2rem auto',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e1b4b 100%)',
        color: '#f1f5f9',
        borderRadius: '0.75rem',
        padding: '2rem',
        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
        border: '1px solid rgba(100, 150, 255, 0.2)',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div
          style={{
            fontSize: '2.5rem',
            marginBottom: '1rem',
          }}
        >
          🏢
        </div>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '600', marginBottom: '0.5rem' }} data-testid="partner-login-title">
          Connexion Partenaire
        </h1>
        <p style={{ color: '#cbd5e1', fontSize: '0.95rem' }}>
          Accès partenaires - Club, Association, Tournoi
        </p>
      </div>

      {/* Messages d'erreur/info */}
      {error && (
        <div
          data-testid="error-message"
          style={{
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#fca5a5',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            lineHeight: '1.5',
          }}
        >
          {error}
        </div>
      )}

      {info && (
        <div
          style={{
            background: 'rgba(34, 197, 94, 0.15)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: '#86efac',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            lineHeight: '1.5',
          }}
        >
          {info}
        </div>
      )}

      {/* Formulaire */}
      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Email */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label
            htmlFor="partner-email"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.95rem',
              fontWeight: '500',
              color: '#e2e8f0',
            }}
          >
            Adresse email
          </label>
          <input
            id="partner-email"
            type="email"
            placeholder="ex: admin@tennis-club.ch"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            data-testid="email-input"
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '1px solid rgba(100, 150, 255, 0.3)',
              background: 'rgba(15, 23, 42, 0.8)',
              color: '#f1f5f9',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(100, 150, 255, 0.6)'
              e.currentTarget.style.background = 'rgba(15, 23, 42, 1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(100, 150, 255, 0.3)'
              e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)'
            }}
          />
        </div>

        {/* Mot de passe */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label
            htmlFor="partner-password"
            style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.95rem',
              fontWeight: '500',
              color: '#e2e8f0',
            }}
          >
            Mot de passe
          </label>
          <input
            id="partner-password"
            type="password"
            placeholder="Minimum 6 caractères"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            data-testid="password-input"
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: '1px solid rgba(100, 150, 255, 0.3)',
              background: 'rgba(15, 23, 42, 0.8)',
              color: '#f1f5f9',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              transition: 'all 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'rgba(100, 150, 255, 0.6)'
              e.currentTarget.style.background = 'rgba(15, 23, 42, 1)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(100, 150, 255, 0.3)'
              e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)'
            }}
          />
          <p
            style={{
              fontSize: '0.8rem',
              color: isPasswordValid ? '#86efac' : '#fca5a5',
              marginTop: '0.25rem',
            }}
          >
            {password.length > 0
              ? `${password.length}/${MAX_PASSWORD_LENGTH} caractères`
              : ''}
          </p>
        </div>

        {/* Bouton connexion */}
        <button
          type="submit"
          disabled={loading || !email || !isPasswordValid}
          data-testid="login-button"
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: loading || !email || !isPasswordValid ? 'not-allowed' : 'pointer',
            opacity: loading || !email || !isPasswordValid ? 0.5 : 1,
            transition: 'all 0.2s',
            marginBottom: '1rem',
          }}
          onMouseEnter={(e) => {
            if (!loading && email && isPasswordValid) {
              e.currentTarget.style.background =
                'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(59, 130, 246, 0.3)'
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && email && isPasswordValid) {
              e.currentTarget.style.background =
                'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
              e.currentTarget.style.boxShadow = 'none'
            }
          }}
        >
          {loading ? 'Connexion en cours...' : 'Se connecter'}
        </button>
      </form>

      {/* Lien vers support */}
      <div
        style={{
          textAlign: 'center',
          fontSize: '0.85rem',
          color: '#94a3b8',
          paddingTop: '1rem',
          borderTop: '1px solid rgba(100, 150, 255, 0.1)',
        }}
      >
        <p>
          Vous n'avez pas accès au Partner Portal?{' '}
          <a 
              style={{
                color: '#60a5fa',
                textDecoration: 'none',
                fontWeight: '500',
              }}
                onClick={handleContactWhatsApp}
              >
                Contacter l'administrateur
          </a>
        </p>
      </div>

      {/* Infos de test (dev only) */}
      {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            background: 'rgba(100, 150, 255, 0.1)',
            border: '1px solid rgba(100, 150, 255, 0.2)',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginTop: '1.5rem',
            fontSize: '0.8rem',
            color: '#cbd5e1',
          }}
        >
          <p style={{ marginBottom: '0.5rem', fontWeight: '600', color: '#60a5fa' }}>
            🧪 Comptes de test (DEV only):
          </p>
          <p>
            <strong>Club Tennis:</strong> club_vd_admin@tennis-vd.ch / Test123!
          </p>
          <p>
            <strong>Badminton:</strong> badminton_ge_admin@badminton-ge.ch / Test123!
          </p>
        </div>
      )}
    </div>
  )
}

export default PartnerLoginPage
